import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { faBullhorn, faEye, faGear, faPlus } from "@fortawesome/pro-regular-svg-icons";

import { isFinishedRunNote } from "@signalist/shared-types";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { ListProcessingStatus, ListStatus } from "@/enum/list.enum";
import { ROUTE_CONFIG } from "@/routes/config";

import { List } from "../schema/list";
import { getListConfigurationStatus } from "../utils/useListConfigurationStatus";
import { describeListSetupRefusal, getListSetupRefusal } from "../utils/listSourceState";
import { listHasLeads } from "../utils/listHasLeads";

import { ListTypeElement } from "./ListTypeElement";
import { ListProcessingStatusChip } from "./ListProcessingStatusChip";
import { ListEnrichmentToggle } from "./ListEnrichmentToggle";
import { ListNoFileHint } from "./ListNoFileHint";
import { ROW_ACTION_BUTTON } from "./ListCampaignSubRows";

/**
 * List name cell: the name links to the list's leads/results view, and hovering the row reveals
 * "View leads" + "List settings" actions overlaid on the right (mirrored by the campaign sub-rows).
 * When the sequencer is active, a third action follows: "Campaign settings" if the list already has
 * a campaign, otherwise "Create new campaign" (which lands on the campaign builder with the
 * Event-vs-Regular branch picker auto-opened via `?createCampaign=true`).
 */
export const ListNameCell: FunctionComponent<{ list: List; isSequencerActive?: boolean }> = ({
	list,
	isSequencerActive = false,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const config = getListConfigurationStatus(list);
	const settingsUrl = `${ROUTE_CONFIG.LISTS}/create/settings/${list.id}`;
	const copywritingUrl = `${ROUTE_CONFIG.LISTS}/create/copywriting/${list.id}`;
	// Counts arrive as strings at runtime (SQL aggregate) — coerce before comparing.
	const hasCampaign = Number(list.campaignCount ?? 0) > 0;
	// When the list already has leads (e.g. moved in from another list), skip the setup redirect
	// `getListConfigurationStatus` produces for a missing/incomplete source and go straight to the
	// Unassigned tab — the leads are there regardless of whether the source is configured.
	const leadsUrl = `${ROUTE_CONFIG.LISTS}/detail/${list.id}?tab=UNASSIGNED`;
	const targetUrl = listHasLeads(list) ? leadsUrl : config.url;

	return (
		<div className="relative flex min-w-0 items-center">
			<ListTypeElement list={list} withoutName />
			<a className="truncate text-brand-light hover:underline" href={targetUrl}>
				{list?.name}
			</a>
			<div className="absolute right-0 top-1/2 hidden -translate-y-1/2 items-center gap-1.5 bg-gradient-to-r from-transparent to-brand-white pl-6 group-hover:flex">
				<Tooltip content={t("list.viewLeads")}>
					<button
						aria-label={t("list.viewLeads")}
						className={ROW_ACTION_BUTTON}
						type="button"
						onClick={() => navigate(targetUrl)}
					>
						<Icon className="w-3 h-3" icon={faEye} />
					</button>
				</Tooltip>
				<Tooltip content={t("list.listSettings")}>
					<button
						aria-label={t("list.listSettings")}
						className={ROW_ACTION_BUTTON}
						type="button"
						onClick={() => navigate(settingsUrl)}
					>
						<Icon className="w-3 h-3" icon={faGear} />
					</button>
				</Tooltip>
				{isSequencerActive ? (
					hasCampaign ? (
						<Tooltip content={t("list.campaignSettings")}>
							<button
								aria-label={t("list.campaignSettings")}
								className={ROW_ACTION_BUTTON}
								type="button"
								onClick={() => navigate(copywritingUrl)}
							>
								<Icon className="w-3 h-3" icon={faBullhorn} />
							</button>
						</Tooltip>
					) : (
						<Tooltip content={t("list.createNewCampaign")}>
							<button
								aria-label={t("list.createNewCampaign")}
								className={ROW_ACTION_BUTTON}
								type="button"
								onClick={() => navigate(`${copywritingUrl}?createCampaign=true`)}
							>
								<Icon className="w-3 h-3" icon={faPlus} />
							</button>
						</Tooltip>
					)
				) : null}
			</div>
		</div>
	);
};

/**
 * Enrichment cell (mockup parity): an Activate/Deactivate hover-swap button for the list's
 * enrichment, a divider, then the processing/config status chip (Done / Add source / Request
 * scraping / Processing / Error …). The button is disabled while the list can't run (archived,
 * special, empty, mid-stop, errored, or still needing configuration) — the chip then carries the
 * actionable next step.
 */
export const EnrichmentCell: FunctionComponent<{
	list: List;
	onToggle: (id: number, status: ListStatus) => Promise<void>;
	onRunNow: (id: number) => void;
}> = ({ list, onToggle, onRunNow }) => {
	const { t } = useTranslation();

	// The overview rows come from `GET list/lean` (plural), which hydrates the light per-source
	// arrays — so config status is safe to derive here (unlike the single lean list; PREC-3621).
	const config = getListConfigurationStatus(list);
	// Every setup step the shared predicate reports blocks activation (PREC-4537), and the toggle
	// says which one is missing instead of being a dead control (PREC-4118).
	const setupRefusal = getListSetupRefusal(config);
	const configBlocked = setupRefusal !== null;

	return (
		<div className="flex items-center gap-2.5 whitespace-nowrap">
			<ListEnrichmentToggle
				configBlocked={configBlocked}
				disabledReason={setupRefusal ? describeListSetupRefusal(setupRefusal, t) : undefined}
				list={list}
				onToggle={onToggle}
			/>
			<span className="h-4 w-px flex-shrink-0 bg-border-light" />
			<ListProcessingStatusChip
				lastActivity={list.lastCronJob?.lastActivity}
				list={list}
				liveViewUrl={list.liveViewUrl}
				overwriteText={
					// PREC-4121: an IDLE list may carry the reason its last run ended ("No source to
					// process", "Run stopped …"); the chip shows it in place of the next-run label.
					// PREC-4470: a DONE list shows only a run-end note, never a stale progress label.
					((list.processingStatus === ListProcessingStatus.PROCESSING ||
						list.processingStatus === ListProcessingStatus.IDLE ||
						list.processingStatus === ListProcessingStatus.ERROR) &&
						list.processingStatusMessage) ||
					(list.processingStatus === ListProcessingStatus.DONE &&
						isFinishedRunNote(list.processingStatusMessage))
						? (list.processingStatusMessage ?? undefined)
						: undefined
				}
				progressPercent={list.processingProgressPercent}
				processingStatus={list.processingStatus}
				status={list.status}
				onRunNow={onRunNow}
			/>
			{config.sourceManualOnly && <ListNoFileHint sourceStepUrl={config.url} />}
		</div>
	);
};
